import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { usersAPI, authAPI, kpisAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Key,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Eye,
  UserCheck,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  Copy,
  Target,
  Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { UserAvatar } from '../components/UserAvatar';
import { AvatarUploadDialog } from '../components/AvatarUploadDialog';
import { GranularPermissionsSection } from '../components/users/GranularPermissionsSection';

const UsersPage = () => {
  const navigate = useNavigate();
  const { isAdmin, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [assignKPIsDialogOpen, setAssignKPIsDialogOpen] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [avatarUser, setAvatarUser] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true,
  });
  const [newPassword, setNewPassword] = useState('');
  const [forceChange, setForceChange] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedKPIs, setSelectedKPIs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, kpisRes] = await Promise.all([
        usersAPI.getAll(),
        kpisAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && user.is_active) ||
                         (filterStatus === 'inactive' && !user.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Role badge helper
  const getRoleBadge = (role) => {
    const config = {
      admin: { label: 'Admin', icon: ShieldCheck, class: 'bg-rose-50 text-rose-700 border-rose-200' },
      user: { label: 'Usuario', icon: UserCheck, class: 'bg-blue-50 text-blue-700 border-blue-200' },
      viewer: { label: 'Viewer', icon: Eye, class: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const c = config[role] || config.user;
    const Icon = c.icon;
    return (
      <Badge variant="outline" className={cn('text-xs', c.class)}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  // Create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAPI.register(formData);
      toast.success('Usuario creado correctamente');
      setCreateDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'user', is_active: true, is_guardia: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  // Edit user
  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      is_guardia: !!user.is_guardia,
    });
    setEditDialogOpen(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersAPI.update(editingUser.id, formData);
      toast.success('Usuario actualizado correctamente');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    } finally {
      setSaving(false);
    }
  };

  // Reset password
  const handleOpenResetPassword = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setForceChange(true);
    setShowPassword(false);
    setResetPasswordDialogOpen(true);
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      await usersAPI.resetPassword(resetPasswordUser.id, newPassword, forceChange);
      toast.success(`Contraseña restablecida para ${resetPasswordUser.name}`);
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
    toast.success('Contraseña copiada al portapapeles');
  };

  // Delete user
  const handleOpenDelete = (user) => {
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    setSaving(true);
    try {
      const response = await usersAPI.delete(deletingUser.id);
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setSaving(false);
    }
  };

  // Assign KPIs
  const handleOpenAssignKPIs = async (user) => {
    setAssigningUser(user);
    try {
      const res = await usersAPI.getAssignedKPIs(user.id);
      setSelectedKPIs(res.data.map(k => k.id));
    } catch {
      setSelectedKPIs(user.assigned_kpi_ids || []);
    }
    setAssignKPIsDialogOpen(true);
  };

  const handleAssignKPIs = async () => {
    setSaving(true);
    try {
      await usersAPI.assignKPIs(assigningUser.id, selectedKPIs);
      toast.success('KPIs asignados correctamente');
      setAssignKPIsDialogOpen(false);
      setAssigningUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar KPIs');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20" />)}
          </div>
        </div>
      </Layout>
    );
  }

  // Access denied for non-admins
  if (!isAdmin()) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-8 max-w-md text-center">
            <ShieldAlert className="w-16 h-16 text-rose-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Acceso Denegado</h2>
            <p className="text-slate-600 mb-4">
              Solo los administradores pueden acceder a la gestión de usuarios.
            </p>
            <Button onClick={() => navigate('/dashboard')}>Ir al Dashboard</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TooltipProvider>
        <div className="space-y-4 sm:space-y-6" data-testid="users-page">
          {/* Header */}
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
                Administración de Usuarios
              </h1>
              <p className="text-slate-500 mt-1 text-sm sm:text-base">
                Gestiona usuarios, roles y permisos
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-role">
                <Shield className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">Usuario</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Users List */}
          <Card>
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Users className="w-5 h-5" />
                Usuarios ({filteredUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {filteredUsers.length === 0 ? (
                <div className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No se encontraron usuarios</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      className={cn(
                        "p-4 transition-colors hover:bg-slate-50",
                        !user.is_active && "bg-slate-50/50"
                      )}
                      data-testid={`user-row-${user.id}`}
                    >
                      {/* Desktop Layout - 3 columnas con anchos fijos */}
                      <div className="hidden sm:grid sm:grid-cols-[1fr_120px_160px] sm:items-center sm:gap-4">
                        {/* Columna 1: Identidad (flexible) */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div 
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => { setAvatarUser(user); setAvatarDialogOpen(true); }}
                            data-testid={`user-avatar-${user.id}`}
                          >
                            <UserAvatar name={user.name} avatarUrl={user.avatar_url} size="md" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "font-medium truncate",
                                !user.is_active && "text-slate-400"
                              )}>
                                {user.name}
                              </span>
                              {user.id === currentUser?.id && (
                                <Badge variant="outline" className="text-[10px] bg-slate-100 flex-shrink-0">Tú</Badge>
                              )}
                              {!user.is_active && (
                                <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 flex-shrink-0">
                                  Inactivo
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 truncate">{user.email}</p>
                          </div>
                        </div>
                        
                        {/* Columna 2: Rol (ancho fijo 120px) */}
                        <div className="flex justify-center">
                          {getRoleBadge(user.role)}
                        </div>
                        
                        {/* Columna 3: Acciones (ancho fijo 160px) */}
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEdit(user)}
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Editar usuario</TooltipContent>
                          </Tooltip>
                          
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenResetPassword(user)}
                                data-testid={`reset-password-${user.id}`}
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Restablecer contraseña</TooltipContent>
                          </Tooltip>
                          
                          {user.role === 'viewer' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenAssignKPIs(user)}
                                  data-testid={`assign-kpis-${user.id}`}
                                >
                                  <Target className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Asignar KPIs (Solo lectura)</TooltipContent>
                            </Tooltip>
                          )}
                          
                          {user.role === 'user' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenAssignKPIs(user)}
                                  data-testid={`assign-kpis-user-${user.id}`}
                                >
                                  <Target className="w-4 h-4 text-blue-500" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Asignar KPIs (Captura)</TooltipContent>
                            </Tooltip>
                          )}
                          
                          {user.id !== currentUser?.id && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                  onClick={() => handleOpenDelete(user)}
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Eliminar usuario</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      
                      {/* Mobile Layout - Stack vertical */}
                      <div className="sm:hidden space-y-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => { setAvatarUser(user); setAvatarDialogOpen(true); }}
                          >
                            <UserAvatar name={user.name} avatarUrl={user.avatar_url} size="md" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "font-medium truncate",
                                !user.is_active && "text-slate-400"
                              )}>
                                {user.name}
                              </span>
                              {user.id === currentUser?.id && (
                                <Badge variant="outline" className="text-[10px] bg-slate-100">Tú</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 truncate">{user.email}</p>
                          </div>
                          {getRoleBadge(user.role)}
                        </div>
                        <div className="flex items-center justify-between pl-13">
                          <div className="flex items-center gap-1">
                            {!user.is_active && (
                              <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500">
                                Inactivo
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(user)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleOpenResetPassword(user)}>
                              <Key className="w-4 h-4" />
                            </Button>
                            {user.role === 'viewer' && (
                              <Button variant="ghost" size="sm" onClick={() => handleOpenAssignKPIs(user)} title="Asignar KPIs (Solo lectura)">
                                <Target className="w-4 h-4" />
                              </Button>
                            )}
                            {user.role === 'user' && (
                              <Button variant="ghost" size="sm" onClick={() => handleOpenAssignKPIs(user)} title="Asignar KPIs (Captura)">
                                <Target className="w-4 h-4 text-blue-500" />
                              </Button>
                            )}
                            {user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-rose-500 hover:text-rose-700"
                                onClick={() => handleOpenDelete(user)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Nuevo Usuario</DialogTitle>
              <DialogDescription>
                Crea un nuevo usuario en el sistema.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">Nombre</Label>
                <Input
                  id="create-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="create-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="create-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-password">Contraseña</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="create-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-role">Rol</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                >
                  <SelectTrigger data-testid="create-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">Usuario</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <Label htmlFor="create-guardia" className="text-sm">Guardia (caseta)</Label>
                  <p className="text-xs text-slate-500">Muestra la vista móvil de caseta en Vigilancia</p>
                </div>
                <Switch
                  id="create-guardia"
                  checked={!!formData.is_guardia}
                  onCheckedChange={(v) => setFormData({ ...formData, is_guardia: v })}
                  data-testid="create-user-guardia"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Creando...' : 'Crear Usuario'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Editar Usuario</DialogTitle>
              <DialogDescription>
                Modifica la información del usuario.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4">
              {/* Avatar section */}
              <div className="flex flex-col items-center gap-2 pb-2">
                <div 
                  className="relative group cursor-pointer"
                  onClick={() => { setAvatarUser(editingUser); setAvatarDialogOpen(true); }}
                  data-testid="edit-user-avatar"
                >
                  <UserAvatar name={editingUser?.name} avatarUrl={editingUser?.avatar_url} size="xl" />
                  <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="w-5 h-5 text-white" />
                  </div>
                </div>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  onClick={() => { setAvatarUser(editingUser); setAvatarDialogOpen(true); }}
                >
                  Cambiar foto
                </button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nombre</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="edit-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Rol</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                  disabled={editingUser?.id === currentUser?.id}
                >
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">Usuario</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                {editingUser?.id === currentUser?.id && (
                  <p className="text-xs text-amber-600">No puedes cambiar tu propio rol</p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-status">Estado activo</Label>
                <Switch
                  id="edit-status"
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                  disabled={editingUser?.id === currentUser?.id}
                  data-testid="edit-user-status"
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <Label htmlFor="edit-guardia" className="text-sm">Guardia (caseta)</Label>
                  <p className="text-xs text-slate-500">Muestra la vista móvil de caseta en Vigilancia</p>
                </div>
                <Switch
                  id="edit-guardia"
                  checked={!!formData.is_guardia}
                  onCheckedChange={(v) => setFormData({ ...formData, is_guardia: v })}
                  data-testid="edit-user-guardia"
                />
              </div>
              {editingUser && (editingUser.role === 'user' || editingUser.role === 'viewer') && (
                <GranularPermissionsSection user={editingUser} />
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                <Key className="w-5 h-5" />
                Restablecer Contraseña
              </DialogTitle>
              <DialogDescription>
                Establece una nueva contraseña para <strong>{resetPasswordUser?.name}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Importante</p>
                    <p>Esta acción quedará registrada en el log de auditoría. El usuario deberá usar la nueva contraseña para acceder.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-password">Nueva Contraseña</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="flex-1"
                    data-testid="new-password-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateRandomPassword}
                    data-testid="generate-password-btn"
                  >
                    Generar
                  </Button>
                </div>
              </div>
              
              {newPassword && showPassword && (
                <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
                  <code className="flex-1 text-sm font-mono">{newPassword}</code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={copyToClipboard}
                    data-testid="copy-password-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="force-change">Forzar cambio en próximo login</Label>
                  <p className="text-xs text-slate-500">El usuario deberá crear una nueva contraseña</p>
                </div>
                <Switch
                  id="force-change"
                  checked={forceChange}
                  onCheckedChange={setForceChange}
                  data-testid="force-change-switch"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleResetPassword} 
                disabled={saving || !newPassword || newPassword.length < 6}
                data-testid="confirm-reset-password"
              >
                {saving ? 'Restableciendo...' : 'Restablecer Contraseña'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-rose-600">
                <AlertCircle className="w-5 h-5" />
                Eliminar Usuario
              </AlertDialogTitle>
              <AlertDialogDescription>
                <p className="mb-2">
                  ¿Estás seguro de que deseas eliminar a <strong>{deletingUser?.name}</strong>?
                </p>
                <p className="text-sm text-slate-500">
                  Esta acción no se puede deshacer. El usuario perderá acceso al sistema inmediatamente.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-rose-600 hover:bg-rose-700"
                disabled={saving}
                data-testid="confirm-delete-user"
              >
                {saving ? 'Eliminando...' : 'Sí, Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Assign KPIs Dialog */}
        <Dialog open={assignKPIsDialogOpen} onOpenChange={setAssignKPIsDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="assign-kpis-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                <Target className="w-5 h-5" />
                Asignar KPIs
              </DialogTitle>
              <DialogDescription>
                Selecciona los KPIs que <strong>{assigningUser?.name}</strong> podrá visualizar.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[300px] overflow-y-auto">
              {kpis.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No hay KPIs disponibles</p>
              ) : (
                kpis.map(kpi => (
                  <div key={kpi.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                    <Checkbox
                      id={`kpi-${kpi.id}`}
                      checked={selectedKPIs.includes(kpi.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedKPIs([...selectedKPIs, kpi.id]);
                        } else {
                          setSelectedKPIs(selectedKPIs.filter(id => id !== kpi.id));
                        }
                      }}
                    />
                    <label htmlFor={`kpi-${kpi.id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium text-sm">{kpi.name}</span>
                      <p className="text-xs text-slate-500">{kpi.area}</p>
                    </label>
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignKPIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAssignKPIs} disabled={saving}>
                {saving ? 'Guardando...' : `Asignar (${selectedKPIs.length})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TooltipProvider>

      {/* Avatar Upload Dialog for Admin */}
      {avatarUser && (
        <AvatarUploadDialog
          open={avatarDialogOpen}
          onOpenChange={setAvatarDialogOpen}
          user={avatarUser}
          onAvatarChanged={(url) => {
            setUsers(prev => prev.map(u => u.id === avatarUser.id ? { ...u, avatar_url: url } : u));
            setAvatarUser(prev => ({ ...prev, avatar_url: url }));
            if (editingUser && editingUser.id === avatarUser.id) {
              setEditingUser(prev => ({ ...prev, avatar_url: url }));
            }
          }}
        />
      )}
    </Layout>
  );
};

export default UsersPage;
